import { defaultMktxDealers } from '../Component/OrderEntry/marketAxessDealers';
import { create } from 'zustand';
import { validateSecurityCusip } from '../http/api';
import {
  defaultListName,
  MAX_LIST_RFQ_BONDS,
  MKTX_DEALER_LIST_MODES,
  MKTX_DEFAULT_EXPIRY_TIME_UTC,
  MKTX_INQUIRY_TYPES,
  MKTX_PRICING_PROCESSES,
  MKTX_STRATEGY_TYPES,
  mktxStrategyUsesDealers,
  ORDER_SIDES,
  PROTOCOL_TYPES,
  TRUMID_PARTIAL_EXECUTION,
  TRUMID_PRICE_TYPES,
  TRUMID_RFQ_INQUIRY_TYPES,
  TRUMID_STIPULATION_TYPES,
} from '../Constants/AppConstants';

/**
 * Default Effective/Expiry time for a MarketAxess bond - copied from MarketAxessFields.jsx
 * (getDefaultExpiryTime/getDefaultEffectiveFromExpiry, the single-bond ticket's own defaulting
 * logic) rather than imported: that file is module-private, and exporting these would mean
 * editing the working single-bond ticket as a side effect of list work.
 */
const getDefaultMktxExpiryTime = () => {
  const [timePart, msPart] = (MKTX_DEFAULT_EXPIRY_TIME_UTC || '20:57:00.000').split('.');
  const [hh, mm, ss] = timePart.split(':').map(Number);
  const ms = Number(msPart || '0');
  const now = new Date();
  const utcDate = new Date(Date.UTC(
    now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), hh, mm, ss, ms
  ));
  return utcDate.toISOString().slice(0, -1);
};

const parseMktxUtcMs = (val) => {
  if (!val) return NaN;
  try {
    const str = String(val).trim();
    const withZ = /[zZ]|[+-]\d{2}:\d{2}$/.test(str) ? str : `${str}Z`;
    const d = new Date(withZ);
    return isNaN(d.getTime()) ? NaN : d.getTime();
  } catch {
    return NaN;
  }
};

const getDefaultMktxEffectiveTime = (expiryIso) => {
  const nowMs = Date.now();
  const expiryMs = parseMktxUtcMs(expiryIso);
  const effectiveMs = Number.isFinite(expiryMs) ? Math.min(nowMs, expiryMs) : nowMs;
  return new Date(effectiveMs).toISOString().slice(0, -1);
};

/**
 * State for the List RFQ tab - one Trumid enquiry covering several bonds at once.
 *
 * In plain terms: the trader fills in the settings that apply to the whole enquiry once
 * (account, dealers, ASAP/BIN, deadline), then adds a row per bond with its own side,
 * quantity and price. Submitting sends all of it as ONE request to POST /order/list.
 *
 * Modelled on Store/actualOrderStore.js, which does the same job for a single bond: same
 * zustand shape, same "data object + fieldErrors + reset" layout, and CUSIP checking goes
 * through the very same validateSecurityCusip call the single-bond flow uses.
 *
 * What differs, and why:
 *  - The bonds live in a `rows` array instead of one flat object, because a list has N of
 *    them. Each row carries its own validation state, since one bad CUSIP must not block
 *    the others.
 *  - The Trumid settings that the backend requires to be IDENTICAL on every bond
 *    (rfqInquiryType <28006>, ttl <28019>, quoteSubmissionDeadline <28020>) are held once
 *    at the top rather than per row, so they cannot disagree by construction. The backend
 *    checks them anyway - see QuoteRequestServiceTrumid.createListRfqDto, which calls
 *    requireSameListLevelValue for exactly these three - because the API is reachable
 *    without this UI.
 */

/**
 * Position of the List RFQ tab inside OrderEntryCore.
 *
 * Held in one place because two files must agree on it: OrderEntryCore renders the tab at
 * this index, and useOrderEntry.handleSubmit routes to the list submit on it. The three
 * existing tabs (0 Bond Order, 1 Bond Characteristic, 2 Bond Ladder) keep their indexes,
 * so nothing about them moves.
 */
export const LIST_RFQ_TAB_INDEX = 3;


/** A bond row that has been typed but not yet checked against the security master. */
const ROW_STATUS = {
  IDLE: 'idle',
  CHECKING: 'checking',
  VALID: 'valid',
  INVALID: 'invalid',
};

let nextRowId = 1;

/**
 * A blank bond row - one line item of the list.
 *
 * Every field here is one the Trumid RFQ spec puts INSIDE the NoRelatedSym <146> repeating
 * group (pp.47-51, marked with a leading arrow), so it genuinely belongs to a single bond
 * and the trader is free to set it differently on each one:
 *
 *   cusip                 48 SecurityID
 *   side                  54 Side
 *   qty                   38 OrderQty
 *   priceType            423 PriceType
 *   stipulationType      234 StipulationValue, inside 232/233 - spread only
 *   allowPartialExecution 28007 AllowPartialFills
 *   venue                453 NoPartyIDs - the disclosed dealers for THIS bond
 *
 * `price` is the odd one out: it is NOT tag 44. createListRfqDto sends 44 and 218 as null,
 * exactly as the single-bond createRfqDto does, so this value never reaches Trumid - it is
 * the order's own price on order_mst. It is kept because the single-bond flow requires it
 * on every RFQ (useOrderEntry requiredFields: account, cusip, qty, price, venue, side).
 *
 * Deliberately NOT offered, because the backend has no field for them and would drop them:
 * 44 Price, 218 Spread, 699/761 Benchmark, and 28002-28004 Passthrough1-3.
 *
 * 64 SettlDate is NOT here: it is calculated rather than chosen, so it lives once on the
 * list (see emptySharedFields) and is stamped onto every bond at submit, exactly as the
 * single-bond ticket does.
 */
/**
 * Whether a price coming back from the security lookup is worth putting in the field.
 *
 * Same rule as hasUsablePrice in ActualOrderTab.jsx (~line 42), which is what the Bond Order
 * tab tests the fetched price with before prefilling. Mirrored rather than imported: that one
 * is a module-private const inside a component, and a store importing from a component would
 * invert the dependency. Guards against the API's "null"/"undefined" strings and zero, all of
 * which would otherwise land in the field as a real-looking price.
 */
const hasUsablePrice = (value) => {
  if (value === null || value === undefined) return false;
  const normalized = String(value).trim().toLowerCase();
  if (!normalized || normalized === 'null' || normalized === 'undefined') return false;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed > 0;
};

export const createEmptyRow = (defaults = {}) => ({
  rowId: nextRowId++,
  cusip: '',
  side: ORDER_SIDES.BUY,
  qty: '',
  price: '',
  priceType: TRUMID_PRICE_TYPES.PRICE,
  stipulationType: null,
  allowPartialExecution: TRUMID_PARTIAL_EXECUTION.YES,
  venue: [],
  bondInventoryId: null,
  minQty: null,
  status: ROW_STATUS.IDLE,
  message: '',
  /**
   * The bond's RFQ status - OPEN, TRADED, CANCELED and so on - as rfq_master holds it.
   *
   * Null on every row the trader is building: a bond that has not been sent has no venue
   * status yet. It is filled only when an existing list is opened to be looked at, and only
   * the read-only grid shows it.
   *
   * Deliberately NOT called `status`: that name is already taken above by ROW_STATUS, which
   * tracks the CUSIP lookup (IDLE, CHECKING, VALID, INVALID) and drives the identifier cell's
   * icon and border. Two different meanings on one key would have the lookup and the venue
   * fighting over the same field.
   */
  rfqStatus: null,

  // >847 TargetStrategy - MarketAxess only, genuinely per bond on the wire (unlike
  // mktxPricingProcess below, which the backend requires identical across the whole list).
  // Defaulted unconditionally, the same way priceType defaults to a Trumid value unconditionally
  // above - inert until the row is actually submitted as a MarketAxess bond. TAKE matches
  // ActualOrderTab's own default for the single-bond ticket.
  strategyType: MKTX_STRATEGY_TYPES.TAKE,

  // >957 StrategyParametersGrp - MarketAxess only, genuinely per bond (no cross-bond
  // agreement check; NewOrderListService.createNewOrderListDto calls
  // FixNewOrderMapper#toFixNewOrder once per bond - the exact same MapStruct mapper the
  // single-bond path uses, "strategy-parameter value conversion" included - so this was
  // already wired end to end on Shared/Backend/Gateway with zero list-frontend work; only
  // the UI to collect a value per row was missing).
  //
  // Plain {paramName: value} map, the same shape as the single-bond ticket's own
  // `actualOrderData.strategyParameters` - deliberately NOT the full definition list (that
  // is fetched per strategy type, not per row, and cached once in ListRfqTab rather than
  // duplicated onto every row that happens to share a type - see mktxStrategyParamDefs).
  // Cleared back to {} whenever a row's strategyType changes (ListRfqBondRow's strategyType
  // onChange), the same "isTypeChanged -> reset" rule ActualOrderTab.loadStrategyParams
  // applies, so a value entered for one strategy can never silently ride along after the
  // trader switches to a different one with different (or no) parameters.
  strategyParameters: {},

  // >20130 per-item dealer override - MarketAxess only, and genuinely per bond (unlike the
  // list-level Dealers field on listRfqData.mktxVenue, which the backend requires identical
  // across every bond). Dealer CODES only, same shape as venue/mktxVenue above - the include/
  // exclude distinction the wire supports (>>20133) is deliberately not exposed here, mirroring
  // the single-bond ticket's own Dealer field, which has no exclude concept either (only
  // "Include Only"/"Include At Least" modes exist there). A dealer picked here is sent with
  // exclude left absent, which >>20133's own spec text defines as "include" - so this reads as
  // "include these dealers on this bond" in per-bond targeting mode. The list-level Dealers
  // field is cleared when this mode is chosen, so the two selections cannot conflict.
  orderDealers: [],

  // >5214 mktxTargetLevel - MarketAxess only, genuinely per bond (no cross-bond agreement
  // check, same as strategyType/orderDealers above - NewOrderListService.toLine reads it
  // straight off each FixNewOrderSingle it is given, the same way it reads strategyType).
  // FixNewOrderList's own class doc calls this "the list's only limit-level concept, since
  // tag 44 Price is out" - >44 is never mapped on either the single or the list path (see
  // that class doc, and the `price` column comment above), so this is the one field that
  // actually reaches the venue as a limit. Optional on the single-bond ticket (not in its
  // requiredFields) and left null here to match - unlike strategyType, there is no sane
  // non-empty default for a limit level.
  mktxTargetLevel: null,

  // >168/>126 EffectiveTime/ExpireTime - MarketAxess only, genuinely per bond (no
  // FixNewOrderList cross-bond agreement check, same as strategyType/orderDealers above).
  // Switched from an earlier list-level design after review (2026-09-28): the single-bond
  // ticket has only one pair of these fields because it IS one item, so it gave no real
  // precedent either way, and the backend places no constraint favouring one level over the
  // other - per-bond was chosen for consistency with the other genuinely-per-item MarketAxess
  // fields on this same row. Defaulted unconditionally like strategyType, computed fresh here
  // (a live "now" calculation via getDefaultMktxExpiryTime/getDefaultMktxEffectiveTime above,
  // copied from MarketAxessFields.jsx) rather than in a later effect, because createEmptyRow
  // already runs exactly once per row at the moment it is actually created - there is no
  // "row already existed, effect fires later" case to coordinate around the way a single
  // list-level default would have needed to.
  ...mktxDefaultTimes(),
  ...defaults,
});

/** Computed once per call so expiryTime/effectiveTime above are derived from the same instant,
 *  not two separate "now" reads a fraction of a millisecond apart. */
function mktxDefaultTimes() {
  const expiryTime = getDefaultMktxExpiryTime();
  return { expiryTime, effectiveTime: getDefaultMktxEffectiveTime(expiryTime) };
}

/**
 * The list-level settings - the ones the spec puts at MESSAGE level, outside the <146>
 * group, so there is exactly one of each per enquiry:
 *
 *   rfqInquiryType            28006 ListInquiryType
 *   quoteSubmissionDeadline   28020 DueInTimeMinutes
 *   ttl                       28019 GoodForTimeMinutes - BIN only
 *
 * `account` is here for a different reason. The spec puts CorpBookId <1> and TsyBookId
 * <20003> inside the group, so per the spec they could differ per bond - but our backend
 * derives both from the one account it resolved for the list, and handleTrumidListProtocol
 * rejects a list whose orders sit on different accounts. So one account per list is our
 * constraint, not Trumid's, and the UI reflects what the backend will actually accept.
 *
 * 28013 AllToAll is not offered at all: createListRfqDto hardcodes 'N' (fully disclosed),
 * exactly as the single-bond path does, so there is nothing for the trader to choose.
 *
 * Defaults match what the single-bond ticket switches to when the trader picks
 * "Trumid RFQ" - see ActualOrderTab's protocol change handler.
 */
/**
 * The bond type as the single-bond ticket reads it.
 *
 * Reads secMasterDTO first and falls back to the top level, then bondType before type -
 * exactly the order ActualOrderTab uses. That order is not arbitrary: measured against dev
 * on 2026-09-11, GET /inventory/validate-identifier returns the type in BOTH places but
 * spelled differently - secMasterDTO.bondType is "MUNI"/"CORP" while the top-level bondType
 * is "Muni"/"CORP". `type` is absent in both. Reading the wrong one still works only because
 * the comparison below is case-insensitive.
 */
const bondTypeOf = (bondDetails) => {
  const sec = bondDetails?.secMasterDTO || bondDetails || {};
  return (sec && (sec.bondType || sec.type)) || '';
};

/**
 * Is this a corporate bond? The list tab's copy of the single ticket's rule.
 *
 * Replicated character for character from isBondCorporate in ActualOrderTab (~line 1300),
 * which is module-private there, so the two screens cannot disagree about what counts as
 * corporate. Note it is a substring test on a lower-cased string, NOT equality with "CORP":
 * that is deliberate in the original and matters - a type such as "Corporate" or a
 * CORP-prefixed subtype passes. An earlier version of this file compared for equality and
 * would have rejected bonds the single ticket accepts.
 *
 * <p>What the two screens then DO differs, and has to. The single ticket silently switches
 * the protocol from Trumid RFQ to CLOB and clears the Trumid fields, because it offers other
 * protocols. The List Order tab is Trumid-only, so there is nothing to switch to and the row
 * is marked invalid instead.
 */
const isBondCorporate = (bondDetails) => {
  const bondTypeRaw = bondTypeOf(bondDetails);
  if (!bondTypeRaw || typeof bondTypeRaw !== 'string') return false;
  return bondTypeRaw.toLowerCase().includes('corp');
};

const emptySharedFields = () => ({
  account: '',
  rfqInquiryType: TRUMID_RFQ_INQUIRY_TYPES.ASAP,
  quoteSubmissionDeadline: 5,
  ttl: null,

  // Chosen on screen rather than assumed, even though Trumid RFQ is the only option today:
  // OrdersCommandHandler.createOrderList rejects every bond whose protocol is not
  // "Trumid RFQ", so this is the one value the backend accepts. It is a field, not a
  // constant, because MarketAxess (and possibly CLOB) are expected to join it later, and a
  // visible protocol also tells the trader which venue's rules the rest of the form follows.
  protocol: PROTOCOL_TYPES.TRUMID_RFQ,

  // Free text for the whole list, capped at 200 characters by the shared OrderComments
  // component. Copied onto every bond at submit rather than sent once - the backend has no
  // list-level record, it stores comments per order (OrdersCommandHandler.buildOrder ->
  // createdOrder.setComments), exactly as a single-bond order does.
  comments: '',

  // The trader's name for the list. Ours only - never sent to Trumid. Sent on every bond like
  // comments, but stored ONCE by the backend (list_order_seq.list_name). Starts on the
  // protocol's default name; the trader may change or clear it (optional unless
  // LIST_RFQ_NAME_REQUIRED is on).
  listName: defaultListName(PROTOCOL_TYPES.TRUMID_RFQ),

  // 64 SettlDate. Held once for the list and NOT typed by the trader, because that is how
  // the single-bond ticket behaves: ActualOrderTab fetches it from
  // fetchBondCalcSettlementDate when the tab opens, shows it read-only, and useOrderEntry
  // re-fetches and overwrites it at submit. The spec would allow it to differ per line
  // item (64 sits inside the <146> group), but matching the existing flow matters more -
  // the date is calculated from the trade date, not chosen.
  settlementDate: null,

  // 5626 MktxPricingProcess - MarketAxess only, and the reason it lives here rather than on
  // each row: FixNewOrderMapper.mktxPricingProcess has no null guard
  // (MKTXPricingProcess.valueOf(orderRequest.getMktxPricingProcess().name())), so a missing
  // value throws rather than being refused cleanly, and NewOrderListService requires it
  // identical across every bond of a list (requireSameListLevelValue) - the same reason
  // rfqInquiryType above is held once rather than per row. Defaulted unconditionally, like
  // rfqInquiryType is, even though only Trumid's fields matter until Protocol is switched.
  // ONESTEP matches ActualOrderTab's own default for the single-bond ticket.
  mktxPricingProcess: MKTX_PRICING_PROCESSES.ONESTEP,

  // 20002/9690 IncludeDealers/NoDealers - MarketAxess only, and list-level for the same
  // structural reason mktxPricingProcess is: NewOrderListService.requireSameListLevelValue
  // requires every bond's dealer targeting to agree, so this is one control rather than the
  // per-row Dealers picker Trumid uses. Include At Least defaults empty pickers to ALL.
  //
  // Required only when the list actually needs it: the single-bond ticket's own rule
  // (optionsHelper.isDealerRequired) makes Dealers/Dealer List Mode required unless
  // strategyType is SEEK/PROVIDE, keyed on strategyType, which is per-row here rather than
  // list-level. listRfqPayload.dealerTargetingApplies generalises that to a list - "at least
  // one row needs it" - and validateListRfq/buildListRfqPayload apply it from there. See
  // that function's own comment for the reasoning.
  mktxVenue: [],
  // Frontend-only choice: list-wide, per-bond, or both dealer groups. Selecting a
  // single scope clears the inactive selections; BOTH preserves both.
  mktxDealerScope: 'LIST',

  // 20002 IncludeDealers - the master switch mktxVenue's dealers actually need: per
  // FixNewOrderList's own class doc, "without it, per-bond dealer inclusions are silently
  // ignored." Stored as the DISPLAY string (MKTX_DEALER_LIST_MODES), exactly as the single-bond
  // ticket stores it in actualOrderData - listRfqPayload converts to the submit form
  // (MKTX_DEALER_LIST_MODE_DISPLAY_TO_SUBMIT) at the same point useOrderEntry does, right before
  // sending. The single ticket defaults this to Include At Least when MarketAxess is chosen.
  mktxDealerListMode: MKTX_DEALER_LIST_MODES.INCLUDE_AT_LEAST,
});

/**
 * Keeps a row's stipulation consistent with its price type.
 *
 * In plain terms: a bond priced on spread needs a stipulation, and a bond priced on price
 * must not carry one.
 *
 * Trumid RFQ spec p.50 marks Stipulations <232/233/234> "Conditionally required when
 * PriceType=6" - so they are cleared on a PRICE bond and defaulted to SPOT on a SPREAD one
 * that has none yet. The single-bond ticket does the same for its one bond by hiding the
 * field unless SPREAD (TruMidRFQFields.showStipulationType); a grid cannot hide one cell
 * without breaking the column, so the cell is disabled instead.
 *
 * Shared by updateRow and applyToAllRows on purpose. They used to differ: editing a row's
 * price type applied the rule, but "Set all: SPREAD" spread the patch raw and left the
 * stipulation null - so the trader saw an enabled, empty, required cell and only found out
 * at submit.
 */
const withStipulationRule = (row) => {
  if (row.priceType !== TRUMID_PRICE_TYPES.SPREAD) {
    return { ...row, stipulationType: null };
  }
  return row.stipulationType
    ? row
    : { ...row, stipulationType: TRUMID_STIPULATION_TYPES.SPOT };
};

const hasMarketAxessDealerTarget = (rows) => {
  const entered = rows.filter((row) =>
    String(row.cusip || '').trim() || String(row.qty || '').trim() || String(row.price || '').trim()
  );
  return (entered.length ? entered : rows).some((row) =>
    mktxStrategyUsesDealers(row.strategyType));
};

const withMktxDealerDefaults = (shared, rows) => {
  if (shared.protocol !== PROTOCOL_TYPES.MARKETAXESS) return { listRfqData: shared, rows };
  return {
    listRfqData: { ...shared, mktxVenue: defaultMktxDealers(shared.mktxVenue, shared.mktxDealerListMode) },
    rows: rows.map((row) => mktxStrategyUsesDealers(row.strategyType)
      ? { ...row, orderDealers: defaultMktxDealers(row.orderDealers, shared.mktxDealerListMode) }
      : row),
  };
};

const withMarketAxessDealerRule = (state, rows) => {
  if (state.listRfqData.protocol !== PROTOCOL_TYPES.MARKETAXESS) return { rows };
  const wasTargeted = hasMarketAxessDealerTarget(state.rows);
  const isTargeted = hasMarketAxessDealerTarget(rows);
  return {
    rows: rows.map((row) => {
      if (!mktxStrategyUsesDealers(row.strategyType)) return { ...row, orderDealers: [] };
      const previous = state.rows.find((item) => item.rowId === row.rowId);
      return (!previous || !mktxStrategyUsesDealers(previous.strategyType))
        ? { ...row, orderDealers: defaultMktxDealers(row.orderDealers,
            state.listRfqData.mktxDealerListMode || MKTX_DEALER_LIST_MODES.INCLUDE_AT_LEAST) }
        : row;
    }),
    listRfqData: isTargeted
      ? wasTargeted ? state.listRfqData : {
          ...state.listRfqData,
          mktxDealerListMode: MKTX_DEALER_LIST_MODES.INCLUDE_AT_LEAST,
          mktxVenue: defaultMktxDealers(state.listRfqData.mktxVenue, MKTX_DEALER_LIST_MODES.INCLUDE_AT_LEAST),
        }
      : {
          ...state.listRfqData,
          mktxVenue: [],
          mktxDealerListMode: null,
        },
  };
};

const cusipRequests = new Map();

// Invalidate only errors whose inputs changed, including related validation inputs.
const clearEditedErrors = (state, next) => {
  if (!next.rows && !next.listRfqData) return next;
  const shared = next.listRfqData || state.listRfqData;
  const rows = next.rows || state.rows;
  const protocolChanged = shared.protocol !== state.listRfqData.protocol;
  if (protocolChanged) cusipRequests.clear();
  if (next.rows) {
    for (const rowId of cusipRequests.keys()) {
      if (!rows.some((row) => row.rowId === rowId)) cusipRequests.delete(rowId);
    }
  }
  const listRfqErrors = { ...state.listRfqErrors };
  Object.keys(shared).forEach((field) => {
    if (shared[field] !== state.listRfqData[field]) delete listRfqErrors[field];
  });
  const rowErrors = {};
  rows.forEach((row) => {
    const previous = state.rows.find((item) => item.rowId === row.rowId);
    const errors = { ...state.rowErrors[row.rowId] };
    if (previous) {
      Object.keys(row).forEach((field) => {
        if (row[field] !== previous[field]) delete errors[field];
      });
      if (row.cusip !== previous.cusip) {
        cusipRequests.delete(row.rowId);
        delete errors.qty;
      }
      if (row.status !== previous.status) delete errors.cusip;
      if (row.minQty !== previous.minQty) delete errors.qty;
      if (row.effectiveTime !== previous.effectiveTime || row.expiryTime !== previous.expiryTime) {
        delete errors.effectiveTime;
        delete errors.expiryTime;
      }
      if (row.strategyType !== previous.strategyType) {
        delete errors.orderDealers;
        delete errors.strategyParameters;
      }
      if (shared.mktxDealerListMode !== state.listRfqData.mktxDealerListMode
          || shared.mktxDealerScope !== state.listRfqData.mktxDealerScope) delete errors.orderDealers;
      // A duplicate error on the other row is outdated when its matching row changes.
      const key = (item) => (item.cusip || '').trim().toUpperCase();
      if (state.rows.some((item) => item.rowId !== row.rowId && key(item) && key(item) === key(previous))
          && !rows.some((item) => item.rowId !== row.rowId && key(item) === key(row))) delete errors.cusip;
    }
    if (Object.keys(errors).length) rowErrors[row.rowId] = errors;
  });
  return {
    listRfqErrors: protocolChanged ? {} : listRfqErrors,
    rowErrors: protocolChanged ? {} : rowErrors,
    ...next,
  };
};

export const useListRfqStore = create((rawSet, get) => {
  const set = (update) => rawSet((state) => clearEditedErrors(state,
    typeof update === 'function' ? update(state) : update));
  return ({
  listRfqData: emptySharedFields(),
  rows: [createEmptyRow(), createEmptyRow()],
  listRfqErrors: {},
  rowErrors: {},

  setListRfqData: (data) =>
    set((state) => {
      const next = { ...state.listRfqData, ...data };
      // Quote firm time <28019> applies to BIN only (Trumid RFQ spec p.46: "The Quote 'Good
      // For' time"), so it is dropped whenever the enquiry is not BIN.
      if (next.rfqInquiryType !== TRUMID_RFQ_INQUIRY_TYPES.BIN) {
        next.ttl = null;
      }
      // Switching protocols takes the same price-type defaults as the single ticket.
      // Keep a filled bond's chosen side; an untouched MarketAxess bond starts with no side.
      const switchingToMarketAxess =
        data.protocol === PROTOCOL_TYPES.MARKETAXESS &&
        state.listRfqData.protocol !== PROTOCOL_TYPES.MARKETAXESS;
      if (switchingToMarketAxess) {
        next.rfqInquiryType = MKTX_INQUIRY_TYPES.BIN;
        next.ttl = null;
        next.mktxDealerListMode = MKTX_DEALER_LIST_MODES.INCLUDE_AT_LEAST;
        next.mktxPricingProcess = MKTX_PRICING_PROCESSES.ONESTEP;
        next.mktxVenue = [];
        next.mktxDealerScope = 'LIST';
      }
      const switchingToTrumid =
        data.protocol === PROTOCOL_TYPES.TRUMID_RFQ &&
        state.listRfqData.protocol !== PROTOCOL_TYPES.TRUMID_RFQ;
      if (switchingToTrumid) {
        next.rfqInquiryType = TRUMID_RFQ_INQUIRY_TYPES.ASAP;
        next.quoteSubmissionDeadline = 5;
        next.ttl = null;
      }
      const rows = switchingToMarketAxess
        ? state.rows.map((row) =>
            ({
              ...row,
              side: row.side || ORDER_SIDES.BUY,
              priceType: TRUMID_PRICE_TYPES.SPREAD,
              stipulationType: null,
              orderDealers: [],
            }))
        : switchingToTrumid
          ? state.rows.map((row) => ({
              ...row,
              side: !row.cusip && !row.qty && !row.price ? ORDER_SIDES.BUY : row.side,
              priceType: TRUMID_PRICE_TYPES.PRICE,
              stipulationType: null,
            }))
          : state.rows;
      return switchingToMarketAxess || next.mktxDealerListMode !== state.listRfqData.mktxDealerListMode
        ? withMktxDealerDefaults(next, rows) : { listRfqData: next, rows };
    }),

  setMktxDealerScope: (scope) =>
    set((state) => {
      if (!['LIST', 'ITEM', 'BOTH'].includes(scope)) return {};
      return {
        listRfqData: {
          ...state.listRfqData,
          mktxDealerScope: scope,
          ...(scope === 'ITEM' ? { mktxVenue: [] } : {}),
        },
        rows: scope === 'LIST'
          ? state.rows.map((row) => ({ ...row, orderDealers: [] }))
          : state.rows,
      };
    }),

  /**
   * Replaces the whole tab with a list that already exists - the bonds and settings of a List
   * RFQ the trader re-initiated from the blotter.
   *
   * In plain terms: drop in one row per bond of that list, and the enquiry settings it was
   * raised with, so the trader is amending a copy of the old list rather than retyping it.
   *
   * Everything it writes is worked out by listRfqPrefill.js; this only applies it, so the
   * mapping stays testable without a store. It REPLACES the rows rather than appending,
   * because the tab it is filling is untouched at that moment - useListRfqPrefill only runs
   * once per opening of the modal, before the trader has typed anything.
   *
   * The ttl rule is repeated from setListRfqData above rather than shared, because that
   * function merges a patch and this one applies a whole prefill; both must leave quote firm
   * time <28019> null on a non-BIN enquiry, and running it here means a prefilled ASAP list
   * cannot arrive carrying a stale BIN firm time.
   *
   * Row-level statuses are deliberately NOT set here: every prefilled row starts at
   * ROW_STATUS.IDLE like a typed one, and useListRfqPrefill puts each through the same
   * validateRowCusip the grid runs after typing pauses. See that file for why.
   */
  applyListPrefill: ({ sharedFields = {}, rows = [], defaultDealers = false } = {}) =>
    set((state) => {
      const nextData = { ...emptySharedFields(), ...sharedFields };
      if (nextData.rfqInquiryType !== TRUMID_RFQ_INQUIRY_TYPES.BIN) {
        nextData.ttl = null;
      }
      const restoredRows = rows.length ? rows : state.rows;
      return {
        // Reinitiation is a new ticket; historical read-only views keep their saved values.
        ...(defaultDealers ? withMktxDealerDefaults(nextData, restoredRows)
          : { listRfqData: nextData, rows: restoredRows }),
        listRfqErrors: {},
        rowErrors: {},
      };
    }),

  /**
   * Adds a bond, copying the previous row's dealing terms.
   *
   * A list is usually uniform - same price type, same side, same partial-fill rule - so
   * carrying those forward saves re-picking them on every bond. They remain fully editable
   * per row, which is the point: the spec puts all of them inside the <146> group, so Trumid
   * accepts them differing bond by bond.
   *
   * Dealers <453> are deliberately NOT carried. They were until now, and it was wrong: who a
   * bond is shown to is a decision about that bond, so inheriting it silently puts an
   * inquiry in front of dealers the trader never chose for it - the one carried field whose
   * default is not merely inconvenient but a live message to a counterparty. A new row starts
   * with an empty, red-bordered Dealers cell, which validateListRfq already requires to be
   * filled before Send.
   *
   */
  addRow: () =>
    set((state) => {
      if (state.rows.length >= MAX_LIST_RFQ_BONDS) return {};

      const last = state.rows[state.rows.length - 1];
      const carried = state.listRfqData.protocol === PROTOCOL_TYPES.MARKETAXESS
        ? { side: ORDER_SIDES.BUY, priceType: TRUMID_PRICE_TYPES.SPREAD, stipulationType: null }
        : last ? {
            priceType: last.priceType,
            stipulationType: last.stipulationType,
            allowPartialExecution: last.allowPartialExecution,
            side: last.side,
          }
        : {};
      return withMarketAxessDealerRule(state, [...state.rows, createEmptyRow(carried)]);
    }),

  /**
   * Sets one per-item field on every row at once.
   *
   * Convenience only - it writes the same value into each row rather than introducing a
   * list-level field, so the payload still carries a genuine per-line-item value for each
   * bond and the trader can change any single row afterwards.
   */
  applyToAllRows: (patch) =>
    set((state) => withMarketAxessDealerRule(state,
      state.rows.map((row) => withStipulationRule({ ...row, ...patch }))
    )),

  removeRow: (rowId) =>
    set((state) => {
      const remaining = state.rows.filter((row) => row.rowId !== rowId);
      // Never leave the grid with nothing to type into.
      return withMarketAxessDealerRule(state, remaining.length ? remaining : [createEmptyRow(
        state.listRfqData.protocol === PROTOCOL_TYPES.MARKETAXESS
          ? { side: ORDER_SIDES.BUY, priceType: TRUMID_PRICE_TYPES.SPREAD, stipulationType: null }
          : {}
      )]);
    }),

  /**
   * Edits one cell. Touching the CUSIP resets that row's validation, because the bond it
   * refers to has changed and the previously fetched bondInventoryId no longer applies.
   */
  updateRow: (rowId, patch) =>
    set((state) => withMarketAxessDealerRule(state,
      state.rows.map((row) => {
        if (row.rowId !== rowId) return row;
        const next = { ...row, ...patch };

        if (patch.cusip !== undefined && patch.cusip !== row.cusip) {
          next.status = ROW_STATUS.IDLE;
          next.message = '';
          next.bondInventoryId = null;
          next.minQty = null;
        }

        // See withStipulationRule - applied whenever the price type moves, so the
        // stipulation can never be left inconsistent with it.
        return patch.priceType !== undefined ? withStipulationRule(next) : next;
      })
    )),

  /**
   * Checks one row's CUSIP against the security master.
   *
   * Uses validateSecurityCusip, the same endpoint the single-bond ticket uses (see
   * actualOrderStore.validateCusip), so a CUSIP that is good on one is good on the other.
   *
   * Differs from actualOrderStore.validateCusip in one respect: that function stores
   * whatever comes back, while this one tests `success !== false` first. The API signals a
   * bad identifier by returning {success:false, message} with HTTP 200 rather than by
   * throwing, so a plain try/catch does not notice it. Per row that matters - an unchecked
   * bad row would otherwise submit with a null bondInventoryId.
   */
  validateRowCusip: async (token, rowId) => {
    const row = get().rows.find((candidate) => candidate.rowId === rowId);
    if (!row) return;

    const request = Symbol();
    const protocol = get().listRfqData.protocol;
    cusipRequests.set(rowId, request);
    const isCurrent = () => cusipRequests.get(rowId) === request
      && get().listRfqData.protocol === protocol
      && get().rows.some((candidate) => candidate.rowId === rowId && candidate.cusip === row.cusip);

    const cusip = (row.cusip || '').trim();
    if (cusip.length < 9) {
      get().updateRow(rowId, {
        status: ROW_STATUS.INVALID,
        message: 'CUSIP must be at least 9 characters',
      });
      return;
    }

    set((state) => ({
      rows: state.rows.map((candidate) =>
        candidate.rowId === rowId
          ? { ...candidate, status: ROW_STATUS.CHECKING, message: '' }
          : candidate
      ),
    }));

    try {
      const bondDetails = await validateSecurityCusip(token, cusip);
      if (!isCurrent()) return;
      if (!bondDetails || bondDetails.success === false) {
        get().updateRow(rowId, {
          status: ROW_STATUS.INVALID,
          message: bondDetails?.message || 'Invalid CUSIP',
          bondInventoryId: null,
        });
        return;
      }

      // Trumid is a corporate-bond venue, so refuse anything else here rather than letting
      // the venue refuse it after the inquiry is already live.
      //
      // Proven on 2026-09-11: a list of 98419MAM2 (CORP) and 64972HRT1 (MUNI) was accepted
      // by us and sent as one spec-correct 35=R, and Trumid answered the muni line item with
      // 297=5 "Rejected. Missing or invalid instrument" while trading the corporate one
      // (quote_req_id 01M286Z0YK8Q0ZK0VJTYX1T238, orders 8193/8194). Nothing was wrong with
      // the message - the venue simply does not carry that instrument - so the only place to
      // catch it is before sending.
      //
      // MarketAxess has no such restriction - ActualOrderTab.jsx's single-bond ticket already
      // treats it that way (`showMarketAxessOption = true; // MarketAxess is now allowed for
      // all bond types.`), so this check is skipped entirely for it rather than applying
      // Trumid's rule to a venue it was never proven against.
      if (protocol === PROTOCOL_TYPES.TRUMID_RFQ && !isBondCorporate(bondDetails)) {
        get().updateRow(rowId, {
          status: ROW_STATUS.INVALID,
          message: `Trumid trades corporate bonds only - ${cusip.toUpperCase()} is ${bondTypeOf(bondDetails) || 'not a corporate bond'}`,
          bondInventoryId: null,
        });
        return;
      }

      // Prefill quantity to the security's minimum when the trader has not typed one, or
      // has typed less than the minimum - lifted from actualOrderStore.validateCusip.
      const current = get().rows.find((candidate) => candidate.rowId === rowId);
      const minQty = bondDetails.minQty ?? null;
      const needsQty =
        minQty != null && (!current?.qty || Number(current.qty) < Number(minQty));

      // Prefill price from the security, the same thing the Bond Order tab does once its
      // lookup returns (ActualOrderTab.jsx ~line 624: `hasUsablePrice(data.price) && !readOnly`
      // -> setActualOrderData({ price })).
      //
      // Differs from the quantity rule directly above, which only fills an empty or
      // below-minimum box: this overwrites whatever is in the price cell, exactly as the
      // single ticket does. That is the right way round - a changed identifier is a different
      // bond, so a price typed against the previous one is stale, not something to preserve.
      // A security with no usable price leaves the cell alone.
      const nextPrice = hasUsablePrice(bondDetails.price) ? `${bondDetails.price}` : null;

      get().updateRow(rowId, {
        status: ROW_STATUS.VALID,
        message: '',
        bondInventoryId: bondDetails.id ?? null,
        minQty,
        ...(needsQty ? { qty: `${minQty}` } : {}),
        ...(nextPrice !== null ? { price: nextPrice } : {}),
      });
    } catch (error) {
      if (!isCurrent()) return;
      get().updateRow(rowId, {
        status: ROW_STATUS.INVALID,
        message: 'Invalid CUSIP or error fetching bond details',
        bondInventoryId: null,
      });
    } finally {
      if (cusipRequests.get(rowId) === request) cusipRequests.delete(rowId);
    }
  },

  setListRfqErrors: (errors) => set({ listRfqErrors: errors }),
  setRowErrors: (errors) => set({ rowErrors: errors }),
  clearListRfqError: (field) =>
    set((state) => ({ listRfqErrors: { ...state.listRfqErrors, [field]: false } })),

  resetListRfq: () =>
    set({
      listRfqData: emptySharedFields(),
      rows: [createEmptyRow(), createEmptyRow()],
      listRfqErrors: {},
      rowErrors: {},
    }),
  });
});

export { ROW_STATUS };
